import type { JSX } from 'react';
import similarProductsBlocker from '@/assets/images/similarProductsBlocker.webp';
import styles from './SimilarProductsInstallSheet.module.css';

interface SimilarProductsInstallSheetProps {
  isDeeplinkLoading: boolean;
  deeplink: string | null;
  imageUrls: string[];
}

const SimilarProductsInstallSheet = ({
  deeplink,
  imageUrls,
}: SimilarProductsInstallSheetProps): JSX.Element => {
  const getPlatform = (): 'ios' | 'android' | 'unknown' => {
    const userAgent = navigator.userAgent || navigator.vendor;

    if (/android/i.test(userAgent)) {
      return 'android';
    }
    if (
      /iPad|iPhone|iPod/.test(userAgent) ||
      (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1)
    ) {
      return 'ios';
    }
    return 'unknown';
  };

  const getStoreUrl = (): string => {
    const platform = getPlatform();
    if (platform === 'ios') {
      return 'https://apps.apple.com/in/app/slayd-app/id6749233916';
    }
    return 'https://play.google.com/store/apps/details?id=com.slayd.tech';
  };

  const handleOpenDeeplink = (deeplinkUrl: string): void => {
    const platform = getPlatform();

    if (platform === 'ios') {
      deeplinkUrl = deeplinkUrl.replace('https://', 'x-safari-https://');
    }
    window.location.href = deeplinkUrl;
  };

  const handleOpenApp = (): void => {
    const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|Line/i.test(
      navigator.userAgent
    );

    // In-app browsers (Instagram, Facebook, etc.):
    // iOS: Try slayd:// custom scheme — opens app directly if installed,
    //       then fall back to ChottuLink/store after timeout.
    // Android: Use intent:// URL which handles app-installed detection natively
    //          and falls back to the Play Store without showing "Page can't be loaded".
    if (isInAppBrowser) {
      const params = new URLSearchParams();
      imageUrls.forEach((imgUrl) => params.append('image_urls', imgUrl));
      const senderId = sessionStorage.getItem('instagramSenderId');
      if (senderId) params.append('sender_id', senderId);
      const pathSuffix = params.toString() ? `?${params.toString()}` : '';

      if (getPlatform() === 'android') {
        // Android in-app browsers (Instagram, etc.) block both custom schemes
        // and intent:// URLs. Go directly to the ChottuLink or Play Store.
        window.location.href = deeplink ?? getStoreUrl();
      } else {
        const customSchemeUrl = `slayd://similar-product/${pathSuffix}`;
        window.location.href = customSchemeUrl;

        setTimeout(() => {
          window.location.href = deeplink ?? getStoreUrl();
        }, 1500);
      }
      return;
    }

    if (!deeplink) return;
    try {
      handleOpenDeeplink(deeplink);
    } catch (error) {
      console.error('ERROR: ', error);
    }
  };

  return (
    <>
      <div className={styles.overlay} />
      <div className={styles.sheet}>
        <div className={styles.content}>
          <img
            src={similarProductsBlocker}
            alt="Download Slayd App"
            className={styles.image}
          />
          <button className={styles.downloadButton} onClick={handleOpenApp}>
            Open in App
          </button>
        </div>
      </div>
    </>
  );
};

export default SimilarProductsInstallSheet;
